import React, { useEffect, useState, useRef, useCallback } from "react";
import { Modal, Badge, Dropdown, Avatar, Spin, notification } from "antd";
import {
  BellOutlined,
  SearchOutlined,
  CloseOutlined,
  DragOutlined,
  MessageOutlined,
  DollarOutlined,
  FileTextOutlined,
  SafetyOutlined,
  CheckOutlined,
} from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import axiosInstance from "../variables/api/axiosInstance";
import CompanySelectorModal from "./CompanySelectorModal";
import { LOGGED_USER } from "../variables/constants";
import { openSse } from "../variables/api/sseClient";
import { styles, userMenuItems } from "./ui/sidebarStyles";
import AppBreadcrumb from "./AppBreadcrumb";
import { auditLogout, LOGOUT_REASONS } from "../services/logoutAuditService";


// Icône + couleur d'une notification selon sa nature (déduite du titre).
const notifIcon = (not) => {
  const t = (not?.titre || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
  if (t.includes("mention") || t.includes("discussion")) return { icon: <MessageOutlined />, color: "#764ba2" };
  if (t.includes("paiement") || t.includes("reglement") || t.includes("commission") || t.includes("decaiss"))
    return { icon: <DollarOutlined />, color: "#0e9f6e" };
  if (t.includes("sinistre") || t.includes("evaluation") || t.includes("expert"))
    return { icon: <FileTextOutlined />, color: "#c2410c" };
  if (t.includes("contrat") || t.includes("renouvel") || t.includes("agrement"))
    return { icon: <SafetyOutlined />, color: "#1890ff" };
  return { icon: <BellOutlined />, color: "#242166" };
};

const NavBar = ({ filteredResults, renderSearchResults, searchValue, setSearchValue }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchVisible, setSearchVisible] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const [latestNotification, setLatestNotification] = useState("");
  const [myNotifications, setMyNotifications] = useState([]);
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const dragRef = useRef({ startX: 0, startY: 0 });
  const modalRef = useRef(null);
  // Dernière notification affichée (codeId) — évite de re-toaster à chaque tour de polling.
  const lastSeenNotifRef = useRef(null);

  // Dragging handlers
  const handleMouseDown = (e) => {
    setIsDragging(true);
    const rect = modalRef.current.getBoundingClientRect();
    dragRef.current = {
      startX: e.clientX - rect.left,
      startY: e.clientY - rect.top,
    };
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    setPosition({
      x: e.clientX - dragRef.current.startX,
      y: e.clientY - dragRef.current.startY,
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const timeSince = (dateString) => {
    const past = new Date(dateString);
    const now = new Date();
    const diffMs = now - past;
    if (diffMs < 0) return "The date is in the future";
    const totalMinutes = Math.floor(diffMs / (1000 * 60));
    const days = Math.floor(totalMinutes / (60 * 24));
    const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
    const minutes = totalMinutes % 60;
    const parts = [];
    if (days > 0) parts.push(`${days} jr${days > 1 ? "s" : ""}`);
    if (hours > 0) parts.push(`${hours} hr${hours > 1 ? "s" : ""}`);
    if (minutes > 0 || parts.length === 0)
      parts.push(`${minutes} min${minutes > 1 ? "s" : ""}`);
    return parts.join(", ");
  };


  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
      return () => {
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging]);

  // Keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault();
        setSearchVisible(true);
      }
      if (e.key === "Escape" && searchVisible) {
        setSearchVisible(false);
        setSearchValue("");
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [searchVisible]);

  // Ferme la palette de recherche à chaque navigation. Nécessaire quand on sélectionne un résultat
  // qui navigue vers la MÊME route (ex. DetailsSinistreV2 → autre dossier via state) : NavBar n'est
  // pas remonté, donc la modale resterait ouverte. location.key change à chaque entrée d'historique.
  useEffect(() => {
    setSearchVisible(false);
    setSearchValue && setSearchValue("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // Mise à jour dynamique des suggestions basée sur filteredResults
  useEffect(() => {
    if (searchValue?.trim()?.length >= 2) {
      setFilteredSuggestions(filteredResults || []);
    } else {
      setFilteredSuggestions([]);
    }
  }, [filteredResults, searchValue]);

  const handleInputChange = (e) => {
    console.log("Input changed:", e);
    const inputValue = e?.target?.value?.toUpperCase() || "";
    setSearchValue(inputValue);
  };

  const renderResults = () => {
    if (!filteredSuggestions.length) {
      return (
        <div style={{ textAlign: "center", padding: "48px 24px", color: "rgba(36, 33, 102, 0.5)" }}>
          <SearchOutlined style={{ fontSize: "48px", marginBottom: "16px" }} />
          <p style={{ margin: 0, fontSize: "14px" }}>Aucun résultat trouvé</p>
        </div>
      );
    }
    return renderSearchResults(filteredSuggestions);
  };

  const logout = async () => {
    await auditLogout(LOGOUT_REASONS.VOLONTAIRE);
    sessionStorage.clear();
    console.log("Déconnexion");
    navigate("/connexion");
  };

  const handleCompanySelect = (company) => {
    console.log("Compagnie sélectionnée:", company?.raisonSociale);
    sessionStorage.setItem("currentCompagnie", JSON.stringify(company));
    setVisible(false);
  };

  const [api, contextHolder] = notification.useNotification();

  const openNotificationWithIcon = (not) => {
    api.info({
      message: `${not?.titre}`,
      description: `${not?.contenu}\n\n${timeSince(not?.createdAt)}${not?.cheminAcces ? "\n\nCliquer pour ouvrir" : ""}`,
      onClose: () => markAsRead(not),
      // Toast cliquable : redirige vers le dossier (chemin d'accès) si renseigné.
      onClick: not?.cheminAcces ? () => handleNotificationClick(not) : undefined,
      style: not?.cheminAcces ? { cursor: "pointer" } : undefined,
    });
  };

  const markAsRead = (not) => {
    axiosInstance
      .get(`/api/notification/read/${not?.codeId}`)
      .then(() => { })
      .catch((error) => console.error("Error fetching notifications:", error));
  };

  // Clic sur une notification : marquée lue, retirée du badge, et redirection vers son chemin
  // d'accès (ex. le dossier sinistre, onglet Discussions) si renseigné.
  const handleNotificationClick = (not) => {
    markAsRead(not);
    setMyNotifications((prev) => (prev || []).filter((n) => n?.codeId !== not?.codeId));
    if (not?.cheminAcces) {
      navigate(not.cheminAcces);
    }
  };

  // Marque toutes les non-lues comme lues (vide le badge).
  const markAllReadNav = (e) => {
    e?.stopPropagation?.();
    axiosInstance
      .put(`/api/notification/read-all/${LOGGED_USER.username}`)
      .then(() => setMyNotifications([]))
      .catch((error) => console.error("read-all error:", error));
  };

  // Récupère les notifications non lues + toast pour toute NOUVELLE (dédup via lastSeenNotifRef).
  const fetchNotifications = useCallback(() => {
    axiosInstance
      .get(`/api/notification/unread/${LOGGED_USER.username}`)
      .then((response) => {
        const data = Array.isArray(response.data) ? response.data : [];
        setMyNotifications(data);
        const newest = data[0];
        if (newest && newest.codeId && newest.codeId !== lastSeenNotifRef.current) {
          openNotificationWithIcon(newest);
          lastSeenNotifRef.current = newest.codeId;
          setLatestNotification(newest);
        }
      })
      .catch((error) => console.error("Error fetching notifications:", error));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Chargement initial + polling de SECOURS (60 s) si le flux SSE est indisponible.
  useEffect(() => {
    fetchNotifications();
    const intervalId = setInterval(fetchNotifications, 60000);
    return () => clearInterval(intervalId);
  }, [fetchNotifications]);

  // SSE : réception INSTANTANÉE sans rafraîchir. Le flux est authentifié par un token de flux à courte
  // durée (openSse) — jamais le JWT de session dans l'URL. À chaque event "notification" → refetch.
  useEffect(() => {
    if (!LOGGED_USER?.username) return undefined;
    const cleanup = openSse("/api/notification/stream", "notification", fetchNotifications);
    return cleanup;
  }, [fetchNotifications]);

  const changeCompanyFunction = () => {
    setVisible(true);
    const codes = LOGGED_USER.agence.validAgrements.map((ag) => ag?.compagnie?.code);
    axiosInstance
      .get(`/api/organisme/all/assurances`)
      .then((res) => {
        setCompanies(res.data.filter((comp) => codes.includes(comp.code)));
        setLoading(false);
      })
      .catch((err) => console.log(err));
  }

  return (
    <>
      <nav style={styles.navbar}>
        {contextHolder}
        <div
          style={styles.searchTrigger}
          onClick={() => setSearchVisible(true)}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "rgba(74, 144, 226, 0.08)";
            e.currentTarget.style.borderColor = "rgba(74, 144, 226, 0.2)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "rgba(36, 33, 102, 0.05)";
            e.currentTarget.style.borderColor = "rgba(36, 33, 102, 0.1)";
          }}
        >
          <SearchOutlined style={{ fontSize: "18px", color: "#242166" }} />
          <span style={{ color: "rgba(36, 33, 102, 0.6)", fontSize: "14px", flex: 1 }}>
            Rechercher...
          </span>
          <span style={styles.kbd}>Ctrl+K</span>
        </div>

        {/* Right Section */}
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          {/* Notifications — liste cliquable (redirige vers le chemin d'accès) */}
          <Dropdown
            trigger={["click"]}
            placement="bottomRight"
            arrow
            dropdownRender={() => (
              <div style={{ width: 380, background: "#fff", borderRadius: 14, boxShadow: "0 10px 34px rgba(0,0,0,0.18)", overflow: "hidden" }}>
                {/* En-tête */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 14px", background: "linear-gradient(135deg, #242166 0%, #667eea 100%)", color: "#fff" }}>
                  <span style={{ fontWeight: 700, fontSize: 14 }}>
                    <BellOutlined style={{ marginRight: 6 }} />
                    Notifications{myNotifications?.length ? ` · ${myNotifications.length}` : ""}
                  </span>
                  {myNotifications?.length > 0 && (
                    <span onClick={markAllReadNav}
                      style={{ fontSize: 12, cursor: "pointer", color: "#fff", opacity: 0.9, display: "inline-flex", alignItems: "center", gap: 4 }}>
                      <CheckOutlined /> Tout marquer lu
                    </span>
                  )}
                </div>

                {/* Liste (non lues) */}
                <div style={{ maxHeight: 400, overflowY: "auto" }}>
                  {(!myNotifications || myNotifications.length === 0) ? (
                    <div style={{ padding: 28, textAlign: "center", color: "#999", fontSize: 13 }}>
                      <BellOutlined style={{ fontSize: 26, color: "#d9d9d9", display: "block", marginBottom: 8 }} />
                      Aucune notification non lue
                    </div>
                  ) : (
                    myNotifications.map((not) => {
                      const { icon, color } = notifIcon(not);
                      return (
                        <div
                          key={not.codeId}
                          onClick={() => handleNotificationClick(not)}
                          style={{ display: "flex", gap: 10, padding: "11px 14px", cursor: "pointer", borderBottom: "1px solid #f4f4f7", background: "#f8f9ff" }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = "#eef0fb")}
                          onMouseLeave={(e) => (e.currentTarget.style.background = "#f8f9ff")}
                        >
                          <div style={{ width: 34, height: 34, borderRadius: 9, background: `${color}18`, color, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 16 }}>
                            {icon}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 700, fontSize: 13, color: "#1f2937" }}>{not.titre}</div>
                            <div style={{ fontSize: 12, color: "#666", marginTop: 1, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                              {not.contenu}
                            </div>
                            <div style={{ fontSize: 11, color: "#9aa0b5", marginTop: 3 }}>
                              {timeSince(not.createdAt)}{not.cheminAcces ? " · cliquer pour ouvrir" : ""}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                {/* Pied : voir tout l'historique */}
                <div
                  onClick={() => navigate("/notifications")}
                  style={{ padding: "11px 14px", textAlign: "center", cursor: "pointer", color: "#242166", fontWeight: 600, fontSize: 13, borderTop: "1px solid #f0f0f0", background: "#fff" }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "#f5f6fb")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "#fff")}
                >
                  Voir tout l'historique →
                </div>
              </div>
            )}
          >
            <Badge count={myNotifications?.length} size="small">
              <div
                style={{ ...styles.iconButton, cursor: "pointer" }}
                onMouseEnter={(e) => e.currentTarget.style.background = "rgba(36, 33, 102, 0.08)"}
                onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
              >
                <BellOutlined style={{ fontSize: "20px", color: "#242166" }} />
              </div>
            </Badge>
          </Dropdown>

          {/* User Dropdown */}
          <Dropdown menu={{ items: userMenuItems(navigate, changeCompanyFunction, logout) }} placement="bottomRight" arrow>
            <Avatar
              style={{
                background: "linear-gradient(135deg, #4a90e2 0%, #357abd 100%)",
                cursor: "pointer",
              }}
            >
              {LOGGED_USER?.personne?.prenom?.charAt(0)}
              {LOGGED_USER?.personne?.nom?.charAt(0)}
            </Avatar>
          </Dropdown>
        </div>
      </nav>

      {/* Fil d'Ariane */}
      <AppBreadcrumb />

      {/* Search Modal */}
      {searchVisible && (
        <>
          <div style={styles.overlay} onClick={() => setSearchVisible(false)} />
          <div
            ref={modalRef}
            style={{
              ...styles.searchModal,
              ...(position.x !== 0 && {
                left: position.x,
                top: position.y,
                transform: "none",
              }),
            }}
          >
            {/* Header */}
            <div style={styles.searchHeader} onMouseDown={handleMouseDown}>
              <DragOutlined style={{ color: "rgba(36, 33, 102, 0.4)", cursor: "move" }} />
              <SearchOutlined style={{ fontSize: "20px", color: "#242166" }} />
              <input
                type="text"
                placeholder="Rechercher..."
                style={styles.searchInput}
                value={searchValue}
                onChange={handleInputChange}
                autoFocus
              />
              <CloseOutlined
                style={{ fontSize: "16px", color: "rgba(36, 33, 102, 0.6)", cursor: "pointer" }}
                onClick={() => {
                  setSearchVisible(false);
                  setSearchValue("");
                }}
              />
            </div>

            {/* Results */}
            <div style={styles.resultsContainer}>{renderResults()}</div>

            {/* Footer hint */}
            <div
              style={styles.resultsContainerCard}
            >
              <span>
                <span style={styles.kbd}>↵</span> Sélectionner
              </span>
              <span>
                <span style={styles.kbd}>ESC</span> Fermer
              </span>
              <span style={{ marginLeft: "auto" }}>
                <DragOutlined /> Déplacer
              </span>
            </div>
          </div>
        </>
      )}

      {/* Company Selector Modal */}
      {loading ? (
        <Modal title="Chargement..." open={visible} closable={false} footer={null} centered>
          <Spin size="large" />
        </Modal>
      ) : (
        <CompanySelectorModal
          visible={visible}
          companies={companies}
          onSelect={handleCompanySelect}
        />
      )}

      <style>
        {`
          ${styles.resultsContainer}::-webkit-scrollbar {
            width: 6px;
          }
          ${styles.resultsContainer}::-webkit-scrollbar-track {
            background: transparent;
          }
          ${styles.resultsContainer}::-webkit-scrollbar-thumb {
            background: rgba(36, 33, 102, 0.2);
            border-radius: 3px;
          }
          ${styles.resultsContainer}::-webkit-scrollbar-thumb:hover {
            background: rgba(36, 33, 102, 0.3);
          }
        `}
      </style>
    </>
  );
};

export default NavBar;
